import { isEmpty, noop, omit } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';
import { useToggle } from 'react-use';
import {
  FilterDropdownItem,
  FilterToolbarGroup,
  SelectedFilters,
} from 'src/components/Filters';
import { QuickFilterToolbarItem } from 'src/components/FilterToolbar/QuickFilterToolbarItem';
import { useDefaultListingFilter } from 'src/components/Listings/ListingFilterBar/hooks/useDefaultListingFilter';
import { useFilterToolbarQuickFilters } from 'src/hooks/useQuickFilters';
import { ListingWithComp } from 'src/tiles/ListingTilesV2';
import { DefaultListingQuery } from 'src/utils/eventQueryUtils';
import {
  CatalogResults,
  EventWithData,
  ListingMetrics,
  ListingQuery,
  UserSetting,
} from 'src/WebApiController';

import { useFilterQueryContext } from '../FilterQueryContext';
import { useInventoryListingFilters } from './MobileInventorySortAndFilterContext.hooks';
import {
  IneventorySortingType,
  InventoryEventSortingCriteria,
  InventoryListingSortingCriteria,
  InventorySortingCriteria,
  ListingTileFilters,
  ListingTileFilterTarget,
} from './MobileInventorySortAndFilterContext.types';
import {
  filterEventByDisplayConfig,
  filterListingByDisplayConfig,
  getDefaultSortingCriteria,
  sortEventWithCriteria,
  sortListingWithCriteria,
  toQuantityFilterString,
} from './MobileInventorySortAndFilterContext.utils';

export type IMobileInventorySortAndFilterContext = {
  // Sorting props
  sortingCriteria: InventorySortingCriteria;
  hasSortingChanged: boolean;
  setSortingCriteria: (_: InventorySortingCriteria) => void;
  withListingSortingCriteria: (
    listings: ListingWithComp[],
    allInventory?: boolean
  ) => ListingWithComp[];
  withEventSortingCriteria: (
    events: EventWithData[],
    eventMetrics?: Record<string, ListingMetrics>,
    catalogResult?: CatalogResults | undefined
  ) => EventWithData[];
  // Filter props
  tempQuery: ListingQuery;
  filters: FilterToolbarGroup[];
  selectedFilters: SelectedFilters;
  quickFilters?: QuickFilterToolbarItem[] | undefined;
  hasFilterApplied: boolean;
  canSaveFilter: boolean;
  setTempQuery: (_: ListingQuery) => void;
  onFilterRemoved: (_: FilterDropdownItem) => void;
  onSaveSelectedFilters: (_: SelectedFilters) => void;
  onSaveFilterQuery: () => void;
  onResetFilters: () => void;
  onResetCustomFilter: () => void;
  // Local listing filters
  listingTileFilters?: ListingTileFilters;
  setListingTileFilters: (_?: ListingTileFilters) => void;
};

const MobileInventorySortAndFilterContext =
  createContext<IMobileInventorySortAndFilterContext>({
    sortingCriteria: {} as InventorySortingCriteria,
    setSortingCriteria: noop,
    withListingSortingCriteria: (_: ListingWithComp[], __?: boolean) => [],
    withEventSortingCriteria: (
      _: EventWithData[],
      __?: Record<string, ListingMetrics>,
      ___?: CatalogResults | undefined
    ) => [],
    hasSortingChanged: false,
    filters: [],
    selectedFilters: {},
    tempQuery: DefaultListingQuery,
    hasFilterApplied: false,
    canSaveFilter: false,
    setTempQuery: noop,
    onFilterRemoved: noop,
    onSaveSelectedFilters: noop,
    onSaveFilterQuery: noop,
    onResetFilters: noop,
    onResetCustomFilter: noop,
    listingTileFilters: {},
    setListingTileFilters: noop,
  });

export const useMobileInventorySortAndFilterContext = () =>
  useContext(MobileInventorySortAndFilterContext);

export const MobileInventorySortAndFilterContextProvider = ({
  children,
  type,
}: PropsWithChildren<{ type: IneventorySortingType }>) => {
  const [sortingCriteria, setSortingCriteria] =
    useState<InventorySortingCriteria>(() => getDefaultSortingCriteria(type));

  const [listingTileFilters, setListingTileFilters] =
    useState<ListingTileFilters>();

  const filterWithListingTileFilters = useCallback(
    (listing: ListingWithComp) => {
      const { sections, listingQuantities } = listingTileFilters ?? {};
      let qualified = true;
      if (sections && !isEmpty(sections) && listing.seating?.section) {
        qualified &&= sections.includes(listing.seating.section);
      }

      if (
        listingQuantities &&
        listingQuantities.quantities &&
        !isEmpty(listingQuantities.quantities)
      ) {
        if (
          listingQuantities.target === ListingTileFilterTarget.All ||
          (listingQuantities.target === ListingTileFilterTarget.MyInventory &&
            !listing.isCompListing) ||
          (listingQuantities.target === ListingTileFilterTarget.CompInventory &&
            !!listing.isCompListing)
        ) {
          qualified &&= listingQuantities.quantities.includes(
            toQuantityFilterString(listing)
          );
        }
      }
      return qualified;
    },
    [listingTileFilters]
  );

  const withListingSortingCriteria = useCallback(
    (
      listings: ListingWithComp[],
      allInventory?: boolean
    ): ListingWithComp[] => {
      const { displayConfig, sorting } =
        sortingCriteria as InventoryListingSortingCriteria;
      return listings
        .filter(filterListingByDisplayConfig(displayConfig.configs))
        .filter(filterWithListingTileFilters)
        .toSorted(sortListingWithCriteria(sorting, allInventory));
    },
    [filterWithListingTileFilters, sortingCriteria]
  );

  const withEventSortingCriteria = useCallback(
    (
      events: EventWithData[],
      eventMetrics?: Record<string, ListingMetrics>,
      catalogResult?: CatalogResults | undefined
    ) => {
      const { displayConfig, sorting } =
        sortingCriteria as InventoryEventSortingCriteria;
      return events
        .filter(filterEventByDisplayConfig(displayConfig.configs, eventMetrics))
        .toSorted(sortEventWithCriteria(sorting, eventMetrics, catalogResult));
    },
    [sortingCriteria]
  );

  const hasSortingChanged = useMemo(() => {
    const defaultSortingCriteria = getDefaultSortingCriteria(type);
    return !isEqual(sortingCriteria, defaultSortingCriteria);
  }, [sortingCriteria, type]);

  const {
    filterQuery,
    initialQuery,
    tempQuery,
    setFilterQuery,
    setTempQuery,
    resetTempQuery,
  } = useFilterQueryContext<ListingQuery>();

  useEffect(() => {
    if (!isEqual(filterQuery, tempQuery)) {
      setTempQuery(filterQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery]);

  const defaultQuickFilters = useDefaultListingFilter({
    initialQuery,
    listingQuery: filterQuery,
  });

  const { quickFilters, onResetAll } = useFilterToolbarQuickFilters({
    quickFiltersStateSetting: UserSetting.QuickFiltersStateInventory,
    customQuickFiltersSetting: UserSetting.QuickFiltersCustomInventory,
    defaultQuickFilters,
    currentQuery: filterQuery,
    initialQuery,
    onSelect: ({ query }) => {
      setFilterQuery(query);
    },
  });

  const { filters, selectedFilters, onFilterRemoved, onSaveSelectedFilters } =
    useInventoryListingFilters(
      initialQuery,
      filterQuery,
      tempQuery,
      setTempQuery,
      resetTempQuery,
      type === IneventorySortingType.Listing
    );

  const onResetFilters = useCallback(() => {
    onSaveSelectedFilters({});
    setTempQuery(initialQuery);
  }, [initialQuery, onSaveSelectedFilters, setTempQuery]);

  const onSaveFilterQuery = useCallback(() => {
    setFilterQuery(tempQuery);
  }, [setFilterQuery, tempQuery]);

  const hasFilterApplied = useMemo(() => {
    const filteredFilters = omit(filterQuery, [
      'sortBy',
      'timezoneOffsetMins',
      'viewMode',
      'eventOrMappingIds',
      'eventTimeFrameFilter',
    ]);
    const filteredDefaultFilters = omit(DefaultListingQuery, [
      'sortBy',
      'timezoneOffsetMins',
      'viewMode',
      'eventOrMappingIds',
      'eventTimeFrameFilter',
    ]);
    const { sections, listingQuantities } = listingTileFilters ?? {};
    const hasInPageFilters =
      !isEmpty(sections) ||
      (listingQuantities &&
        listingQuantities.quantities &&
        !isEmpty(listingQuantities.quantities));
    return Boolean(
      !isEqual(filteredFilters, filteredDefaultFilters) || hasInPageFilters
    );
  }, [filterQuery, listingTileFilters]);

  const canSaveFilter = useMemo(
    () => !isEqual(filterQuery, tempQuery),
    [filterQuery, tempQuery]
  );

  return (
    <MobileInventorySortAndFilterContext.Provider
      value={{
        sortingCriteria,
        hasSortingChanged,
        setSortingCriteria,
        withListingSortingCriteria,
        withEventSortingCriteria,
        tempQuery,
        filters,
        quickFilters,
        selectedFilters,
        hasFilterApplied,
        canSaveFilter,
        setTempQuery,
        onFilterRemoved,
        onSaveSelectedFilters,
        onSaveFilterQuery,
        onResetFilters,
        onResetCustomFilter: onResetAll,

        listingTileFilters,
        setListingTileFilters,
      }}
    >
      <LocalEventMobileScrollContextProvider>
        {children}
      </LocalEventMobileScrollContextProvider>
    </MobileInventorySortAndFilterContext.Provider>
  );
};

type ILocalEventMobileScrollContext = {
  isAtTop: boolean;
  isAtBottom: boolean;
  setIsAtTop: (_: boolean) => void;
  setIsAtBottom: (_: boolean) => void;
};

const LocalEventMobileScrollContext =
  createContext<ILocalEventMobileScrollContext>({
    isAtTop: true,
    isAtBottom: false,
    setIsAtTop: noop,
    setIsAtBottom: noop,
  });

export const LocalEventMobileScrollContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [isAtTop, setIsAtTop] = useToggle(true);
  const [isAtBottom, setIsAtBottom] = useToggle(false);

  return (
    <LocalEventMobileScrollContext.Provider
      value={{ isAtTop, isAtBottom, setIsAtTop, setIsAtBottom }}
    >
      {children}
    </LocalEventMobileScrollContext.Provider>
  );
};

export const useLocalEventMobileScrollContext = () => {
  const context = useContext(LocalEventMobileScrollContext);
  if (!context) {
    throw new Error(
      'useLocalEventMobileScrollContext must be used within LocalEventMobileScrollContextProvider'
    );
  }
  return context;
};
